/**
 * /policies/eos-rate-matrix — Per-LE rate-matrix editor
 * (M3X.EOS §1.2 + F.1).
 *
 * Visual grid: 8 trigger rows × 4 default tier columns (0-2y /
 * 2-5y / 5-10y / 10-99y). Each cell renders the customer-policy
 * percent + the Mutaba'aa floor + an inline guard if the user
 * tries to save below floor.
 *
 * Saves go through POST /api/eos/rate-matrix/upsert which runs
 * Mutaba'aa floor enforcement at save time (commandment 45 —
 * mode-invariant). HTTP 422 from the backend = floor violation;
 * we render the violation message inline next to the cell.
 *
 * "Seed defaults" button POSTs to /seed-default for tenants whose
 * LE has no matrix configured.
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { api, type EosRateMatrixCellDTO } from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

const TRIGGER_LABELS: Record<string, { en: string; ar: string }> = {
  resignation: { en: "Resignation", ar: "استقالة" },
  termination_without_cause: {
    en: "Termination (without cause)",
    ar: "إنهاء (بدون سبب)",
  },
  termination_with_cause: {
    en: "Termination (with cause / Article 80)",
    ar: "إنهاء (بسبب / المادة ٨٠)",
  },
  end_of_contract: { en: "End of contract", ar: "انتهاء العقد" },
  deceased: { en: "Deceased", ar: "متوفى" },
  absconded: { en: "Absconded", ar: "هارب من العمل" },
  retirement: { en: "Retirement", ar: "تقاعد" },
  mutual: { en: "Mutual separation", ar: "إنهاء بالتراضي" },
};

const TIER_ORDER = ["0-2", "2-5", "5-10", "10-99"];

export function EosRateMatrixPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const legalEntityId = useLegalEntityId();
  const qc = useQueryClient();

  const cellsQ = useQuery({
    queryKey: ["eos-rate-matrix", legalEntityId],
    queryFn: () => api.listEosRateMatrix(legalEntityId ?? ""),
    enabled: Boolean(legalEntityId),
  });

  const [editing, setEditing] = useState<{
    trigger: string;
    tier: string;
    value: string;
  } | null>(null);
  const [vetoMessage, setVetoMessage] = useState<{
    trigger: string;
    tier: string;
    message: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  if (!legalEntityId) {
    return (
      <ErrorPanel
        message={t(
          "eos_rate_matrix.no_le",
          "Please select a legal entity to view its rate matrix.",
        )}
      />
    );
  }

  if (cellsQ.isLoading) return <Loading />;
  if (cellsQ.error)
    return <ErrorPanel message={String((cellsQ.error as Error).message)} />;

  const cells = (cellsQ.data ?? []) as EosRateMatrixCellDTO[];
  const cellMap = new Map<string, EosRateMatrixCellDTO>();
  for (const c of cells) {
    cellMap.set(`${c.trigger_type}|${c.tier_label}`, c);
  }

  async function handleSeed() {
    if (!legalEntityId) return;
    setBusy(true);
    try {
      await api.seedEosRateMatrixDefault(legalEntityId);
      qc.invalidateQueries({ queryKey: ["eos-rate-matrix"] });
    } finally {
      setBusy(false);
    }
  }

  async function handleSave(
    trigger: string,
    tier: string,
    pctStr: string,
    minYears: number,
    maxYears: number,
  ) {
    if (!legalEntityId) return;
    setBusy(true);
    setVetoMessage(null);
    try {
      await api.upsertEosRateMatrixCell({
        legal_entity_id: legalEntityId,
        trigger_type: trigger,
        tier_label: tier,
        min_years: minYears,
        max_years: maxYears,
        percent: pctStr,
      });
      qc.invalidateQueries({ queryKey: ["eos-rate-matrix"] });
      setEditing(null);
    } catch (e) {
      const msg = String(e);
      setVetoMessage({ trigger, tier, message: msg });
    } finally {
      setBusy(false);
    }
  }

  // Tier ranges (min/max) for new cells when seeded yet
  const tierRanges: Record<string, { min: number; max: number }> = {
    "0-2": { min: 0, max: 2 },
    "2-5": { min: 2, max: 5 },
    "5-10": { min: 5, max: 10 },
    "10-99": { min: 10, max: 99 },
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(
          "eos_rate_matrix.title",
          "EOS Rate Matrix",
        )}
        subtitle={t(
          "eos_rate_matrix.subtitle",
          "Customer-policy gratuity rates above the Article 84/85 statutory floor. Mutaba'aa enforces the floor at save time.",
        )}
        actions={
          cells.length === 0 ? (
            <button
              type="button"
              onClick={handleSeed}
              disabled={busy}
              className="rounded-md bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-blue/90 disabled:opacity-50"
            >
              {t(
                "eos_rate_matrix.seed_default",
                "Seed defaults (32 cells at floor)",
              )}
            </button>
          ) : null
        }
      />

      {cells.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-900">
            {t(
              "eos_rate_matrix.empty",
              "No rate matrix configured. Click 'Seed defaults' to create 32 cells (8 triggers × 4 tiers) at the Article 84/85 statutory floor.",
            )}
          </p>
        </div>
      )}

      {cells.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-surface-default">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-subtle">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-text-secondary">
                  {t("eos_rate_matrix.trigger", "Trigger")}
                </th>
                {TIER_ORDER.map((tier) => (
                  <th
                    key={tier}
                    className="px-3 py-2 text-left font-semibold text-text-secondary"
                  >
                    {t("eos_rate_matrix.tier", "Tier")} {tier}y
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.keys(TRIGGER_LABELS).map((trigger) => (
                <tr key={trigger} className="border-t border-slate-200">
                  <td className="px-3 py-3 font-medium text-text-primary">
                    {isAr
                      ? TRIGGER_LABELS[trigger].ar
                      : TRIGGER_LABELS[trigger].en}
                  </td>
                  {TIER_ORDER.map((tier) => {
                    const cell = cellMap.get(`${trigger}|${tier}`);
                    const isEditing =
                      editing?.trigger === trigger && editing?.tier === tier;
                    const veto =
                      vetoMessage?.trigger === trigger &&
                      vetoMessage?.tier === tier
                        ? vetoMessage.message
                        : null;
                    if (!cell) {
                      return (
                        <td
                          key={tier}
                          className="px-3 py-3 text-text-tertiary"
                        >
                          —
                        </td>
                      );
                    }
                    const floorPct = Number(cell.statutory_floor_percent);
                    const currentPct = Number(cell.percent);
                    const atFloor = currentPct === floorPct;
                    return (
                      <td key={tier} className="px-3 py-3">
                        {isEditing ? (
                          <div className="space-y-1">
                            <input
                              type="number"
                              min="0"
                              max="200"
                              step="0.5"
                              value={editing.value}
                              onChange={(e) =>
                                setEditing({
                                  ...editing,
                                  value: e.target.value,
                                })
                              }
                              onBlur={() => {
                                const range = tierRanges[tier];
                                handleSave(
                                  trigger,
                                  tier,
                                  editing.value,
                                  range.min,
                                  range.max,
                                );
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  const range = tierRanges[tier];
                                  handleSave(
                                    trigger,
                                    tier,
                                    editing.value,
                                    range.min,
                                    range.max,
                                  );
                                } else if (e.key === "Escape") {
                                  setEditing(null);
                                  setVetoMessage(null);
                                }
                              }}
                              autoFocus
                              className="w-20 rounded border border-brand-blue px-2 py-1"
                              disabled={busy}
                            />
                            <div className="text-[10px] text-text-tertiary">
                              {t("eos_rate_matrix.floor", "Floor")}: {floorPct}%
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              setEditing({
                                trigger,
                                tier,
                                value: cell.percent,
                              })
                            }
                            className="flex flex-col items-start text-start"
                          >
                            <span
                              className={`font-mono text-lg ${atFloor ? "text-amber-700" : "text-emerald-700"}`}
                            >
                              {currentPct}%
                            </span>
                            <span className="text-[10px] text-text-tertiary">
                              {t("eos_rate_matrix.floor", "Floor")}: {floorPct}%
                            </span>
                          </button>
                        )}
                        {veto && (
                          <div className="mt-1 rounded bg-rose-50 border border-rose-200 p-1 text-[10px] text-rose-700">
                            {veto}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-surface-subtle p-4 text-xs text-text-secondary">
        <p>
          {t(
            "eos_rate_matrix.help",
            "Click any percent to edit. Saves below the floor are vetoed by Mutaba'aa (mode-invariant per Commandment 45). Amber = at floor; emerald = above floor (customer-policy uplift).",
          )}
        </p>
      </div>
    </div>
  );
}

export default EosRateMatrixPage;
